import type { Color } from '@flobi/artwork-core';
import { useRef, useState } from 'react';
import { colorToHex, rgba } from '../../theme';
import { ColourPanel } from '../chrome/Colour';
import { Button, NumberField, Section } from '../../ui/controls';
import { Popover, useAnchor } from '../../ui/popover';
import { tip } from '../../ui/tooltip';
import { usePress } from '../press';
import { colourFrom, shownColour } from '../ink';
import type { ColorPlace, SelectionColor } from '../selectionColors';
import { HexField } from './Paint';

/** How many colours show before "Show all". */
export const FIRST_COLORS = 8;

/**
 * Selection colours, Figma's way: every colour worn inside a group, an artboard or several
 * layers, once each. Changing one recolours every layer that wears it, as one undo step.
 */
export function SelectionColors({ colors, onChange }: {
  colors: readonly SelectionColor[];
  onChange: (places: readonly ColorPlace[], color: Color) => void;
}) {
  const [all, setAll] = useState(false);
  if (!colors.length) return null;
  const shown = all ? colors : colors.slice(0, FIRST_COLORS);
  return (
    <Section title="Selection colors" id="context-selection-colors">
      <div className="flex flex-col gap-[6px]">
        {/* Keyed by place in the list, so a picker stays open while its colour changes. */}
        {shown.map((entry, index) => <ColorRow key={index} entry={entry} onChange={onChange} />)}
      </div>
      {colors.length > FIRST_COLORS ? (
        <Button size="small" full onClick={() => setAll((was) => !was)}>
          {all ? 'Show fewer' : `Show all ${colors.length} colors`}
        </Button>
      ) : null}
    </Section>
  );
}

function ColorRow({ entry, onChange }: { entry: SelectionColor; onChange: (places: readonly ColorPlace[], color: Color) => void }) {
  const [anchor, setAnchor] = useAnchor<HTMLButtonElement>();
  const [open, setOpen] = useState(false);
  const press = usePress();
  // While the picker is open it keeps recolouring the layers it opened on: dragging through
  // another listed colour must not take that colour's layers along too.
  const held = useRef<readonly ColorPlace[] | null>(null);
  const places = () => held.current ?? entry.places;
  const { color } = entry;
  const alpha = color.a ?? 1;
  const hex = colorToHex(color);
  const shown = shownColour(press, color);
  const set = (next: Color) => onChange(places(), next);
  const toggle = (next: boolean) => {
    held.current = next ? entry.places : null;
    setOpen(next);
  };
  const uses = new Set(entry.places.map((place) => place.id)).size;
  return (
    <div className="paint-control">
      <button
        ref={setAnchor}
        type="button"
        aria-label={`Selection color ${hex}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => toggle(!open)}
        {...tip(`Used by ${uses} ${uses === 1 ? 'layer' : 'layers'}`)}
        className={`paint-swatch relative shrink-0 overflow-hidden transition-shadow ${open ? 'ring-[2px] ring-accent/55' : ''}`}
        style={{ background: 'repeating-conic-gradient(#c8c8c8 0% 25%, #ffffff 0% 50%) 50% / 8px 8px', boxShadow: 'inset 0 0 0 0.5px rgb(0 0 0 / 0.25)' }}
      >
        <span aria-hidden className="absolute inset-0" style={{ background: colorToHex(shown), opacity: alpha }} />
      </button>
      <HexField hex={hex} onChange={(next) => set({ ...rgba(next), a: alpha })} label={`Selection color ${hex} hex`} />
      <NumberField className="paint-opacity" label={`Selection color ${hex} opacity`} value={Math.round(alpha * 100)} suffix="%" min={0} max={100} decimals={0} onChange={(value) => set({ ...color, a: value / 100 })} />
      <Popover open={open} anchor={anchor} onClose={() => toggle(false)} placement="left" align="start" width={304} label="Selection color" ownsKeyboard>
        <ColourPanel
          title="Selection color"
          onClose={() => toggle(false)}
          value={hex}
          ink={color.cmyk}
          alpha={alpha}
          onAlpha={(a) => set({ ...color, a })}
          showing
          onChange={(next, ink) => set(colourFrom(next, ink, alpha))}
        />
      </Popover>
    </div>
  );
}
